import { ActionMenuItem } from '@if/mui'
import { useEffect, useState, useMemo } from 'react'
import { EPaymentPlan, EProvince } from '@if/core/types'
import { UseFormSetValue } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { PersonalLoanCalculatorSchemaType } from '@src/types/PersonalLoanCalculatorSchema'
import { ETerms } from '@src/types/Constants'
import { useGetPossibleTerms } from '@src/data/api/worksheet-api/worksheet-api'
import { isAfter, isValid } from '@if/core/date'
import { FormatPaymentPlanForList } from '@src/data/worksheet-selectors'
import { Merchant } from '@src/types/Merchant'
import { MerchantPaymentPlan } from '@src/types/MerchantPaymentPlan'
import { createComputeMonthTermFrequency, shouldAutoSelectTerm } from './personalLoanCalculator-utils'

interface TermOption {
  label: string
  value: number
}

interface PaymentOption {
  label: string
  value: EPaymentPlan | string
  id: string
}

interface UseAutoSelectionProps {
  isDataLoading: boolean
  merchantPayments?: PaymentOption[]
  loanTermChoicesList: TermOption[]
  paymentPlanId?: string
  term?: number
  setValue: UseFormSetValue<PersonalLoanCalculatorSchemaType>
}

export const useAutoSelection = ({
  isDataLoading,
  merchantPayments,
  loanTermChoicesList,
  paymentPlanId,
  term,
  setValue,
}: UseAutoSelectionProps) => {
  const [previousPaymentPlanId, setPreviousPaymentPlanId] = useState<string | null>(null)

  useEffect(() => {
    if (!isDataLoading && merchantPayments) {
      if (!paymentPlanId && merchantPayments.length > 0) {
        const firstPaymentPlan = merchantPayments[0].value as EPaymentPlan
        setValue('paymentPlanId', firstPaymentPlan)
        setPreviousPaymentPlanId(firstPaymentPlan)
      }

      const isSelectedTermStillOffered = loanTermChoicesList.some((option) => option.value === term)
      if (
        loanTermChoicesList.length > 0 &&
        shouldAutoSelectTerm(term, paymentPlanId, previousPaymentPlanId, isSelectedTermStillOffered)
      ) {
        // Pre-select the longest available term so the estimate opens on the lowest payment.
        const longestTerm = loanTermChoicesList.reduce((max, option) => (option.value > max.value ? option : max))
        setValue('term', longestTerm.value)
      }

      if (paymentPlanId && paymentPlanId !== previousPaymentPlanId) {
        setPreviousPaymentPlanId(paymentPlanId)
      }
    }
  }, [isDataLoading, merchantPayments, loanTermChoicesList, paymentPlanId, term, setValue, previousPaymentPlanId])

  return {
    previousPaymentPlanId,
  }
}

interface UsePossibleTermsProps {
  amountRequested?: number
  paymentPlanId?: string
  creditRatingInterestRate: number
  merchantId: string
  /** Provincial rate caps decide which terms stay legal. */
  provinceIso?: EProvince
  includeInsurance?: boolean
  isJointCoverage?: boolean
  /** The date the estimate priced on, so the terms answer for the same loan. */
  firstPaymentOn?: string
}

/**
 * Loads the terms the merchant/plan allows, so the Loan Term dropdown can be
 * populated. Stays live (driven by the current amount/plan/rate) — it's option
 * loading for a field the user selects from, not the payment estimate.
 */
export const usePossibleTerms = ({
  amountRequested,
  paymentPlanId,
  creditRatingInterestRate,
  merchantId,
  provinceIso,
  includeInsurance = false,
  isJointCoverage = false,
  firstPaymentOn,
}: UsePossibleTermsProps) => {
  const computeMonthTermFrequency = useMemo(() => {
    if (amountRequested && merchantId && paymentPlanId && creditRatingInterestRate && provinceIso && firstPaymentOn) {
      return createComputeMonthTermFrequency(
        merchantId,
        provinceIso,
        paymentPlanId,
        creditRatingInterestRate,
        amountRequested,
        includeInsurance,
        isJointCoverage,
        firstPaymentOn,
      )
    }
    return null
  }, [
    amountRequested,
    paymentPlanId,
    creditRatingInterestRate,
    merchantId,
    provinceIso,
    includeInsurance,
    isJointCoverage,
    firstPaymentOn,
  ])

  const [possibleTerms] = useGetPossibleTerms(computeMonthTermFrequency, amountRequested ?? 0)
  return possibleTerms
}

interface UsePaymentOptionsProps {
  merchant: Merchant | null
}

export const usePaymentOptions = ({ merchant }: UsePaymentOptionsProps) => {
  const { t } = useTranslation()

  const merchantPaymentPlans = useMemo(() => {
    return merchant?.paymentPlans ?? []
  }, [merchant?.paymentPlans])

  const merchantPayments = useMemo<PaymentOption[]>(() => {
    const now = new Date()
    const options: PaymentOption[] = [
      { value: EPaymentPlan.RegularDailyInterests, label: EPaymentPlan.RegularDailyInterests, id: merchant?.id ?? '' },
    ]
    merchantPaymentPlans.forEach((plan) => {
      const activeFrom = new Date(plan.activeFrom)
      const expiresOn = plan.expiresOn ? new Date(plan.expiresOn) : null
      const isActive =
        isValid(activeFrom) &&
        !isAfter(activeFrom, now) &&
        (expiresOn === null || (isValid(expiresOn) && isAfter(expiresOn, now)))
      if (isActive) {
        options.push({ value: plan.id, label: FormatPaymentPlanForList(plan), id: plan.id })
      }
    })
    return options
  }, [merchant?.id, merchantPaymentPlans])

  const merchantPaymentOptions = useMemo(
    () =>
      merchantPayments?.map((item) => (
        <ActionMenuItem key={item.value} value={item.value}>
          {item.label.includes(EPaymentPlan.RegularDailyInterests)
            ? t(`EPaymentPlan.${EPaymentPlan.RegularDailyInterests}`, { ns: 'enum' })
            : item.label}
        </ActionMenuItem>
      )),
    [merchantPayments, t],
  )

  return {
    merchantPaymentPlans,
    merchantPayments,
    merchantPaymentOptions,
  } as const
}

interface UseTermOptionsProps {
  paymentPlanId?: EPaymentPlan
  merchantPaymentPlans: MerchantPaymentPlan[]
  possibleTerms?: { monthlyTerms?: { term: number }[] }
  maxTermForAmount: number
}

export const useTermOptions = ({
  paymentPlanId,
  merchantPaymentPlans,
  possibleTerms,
  maxTermForAmount,
}: UseTermOptionsProps) => {
  const { t } = useTranslation()
  const [stableTermOptions, setStableTermOptions] = useState<React.ReactElement[]>([])

  const loanTermChoicesList = useMemo(() => {
    if (paymentPlanId && paymentPlanId !== EPaymentPlan.RegularDailyInterests) {
      const planTerms = [
        ...new Set(
          merchantPaymentPlans
            .filter((plan) => plan.id === paymentPlanId && plan.loanTerm && plan.loanTerm <= maxTermForAmount)
            .map((plan) => plan.loanTerm),
        ),
      ].sort((a, b) => a - b)
      if (planTerms.length > 0) {
        return planTerms.map((loanTerm) => ({
          label: `${loanTerm} ${t('common.months')}`,
          value: loanTerm,
        })) as TermOption[]
      }
    }

    return (possibleTerms?.monthlyTerms
      ?.filter((item) => item.term >= (ETerms.twelveMonths as number) && item.term <= maxTermForAmount)
      .map((item) => ({
        label: `${item.term} ${t('common.months')}`,
        value: item.term,
      })) || []) as TermOption[]
  }, [paymentPlanId, merchantPaymentPlans, possibleTerms, maxTermForAmount, t])

  const termOptions = useMemo(
    () =>
      loanTermChoicesList?.map((loanTerm: TermOption) => (
        <ActionMenuItem key={loanTerm.value} value={loanTerm.value}>
          {loanTerm.label}
        </ActionMenuItem>
      )) || [],
    [loanTermChoicesList],
  )

  useEffect(() => {
    if (termOptions.length > 0) {
      setStableTermOptions(termOptions)
    }
  }, [termOptions])

  return {
    loanTermChoicesList,
    termOptions,
    stableTermOptions,
  } as const
}
